import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { nanoid } from 'nanoid';

import {
  ToolCategory,
  IResult,
  IRephrasedResult,
  ResultType,
  IValues,
} from '../../utils/common/types';
import { FORM_INITIAL_VALUES } from '../../constants';
import { handleScroll } from '../../utils/common/scroll';

type Store = {
  formValues: IValues;
  createPageCategoryIndex: number;
  setCreatePageCategoryIndex: (i: number) => void;
  results: Record<ToolCategory, IResult[]>;
  recents: IResult[];
  resultsAreGenerating: boolean;
  moreResultsAreGenerating: boolean;
  setResultsAreGenerating: (value: boolean) => void;
  setMoreResultsAreGenerating: (value: boolean) => void;
  addResults: (item: { category: ToolCategory; newResults: IResult[]; replace?: boolean }) => void;
  addRephrases: (item: {
    category: ToolCategory;
    rephrasedResults: IRephrasedResult[];
    id: string;
  }) => void;
  setIsRephrasing: (item: { category: ToolCategory; id: string; value: boolean }) => void;
  addRecents: (item: { category: ToolCategory; id: string }) => void;
  validationError: boolean;
  genericError: string | JSX.Element | JSX.Element[];
  showValidationError: (error: boolean) => void;
  showGenericError: (error: string | JSX.Element | JSX.Element[]) => void;
  setFormValues: (values: IValues) => void;
  setFormValue: ({ name, value }: { name: string; value: string }) => void;
};

const initialState = {
  formValues: FORM_INITIAL_VALUES,
  createPageCategoryIndex: 0,
  results: {},
  recents: [],
  resultsAreGenerating: false,
  moreResultsAreGenerating: false,
  validationError: false,
  genericError: '',
};

function findResultToBeAddedToRecents(results: IResult[], recents: IResult[], id: string) {
  const resultsAndRecents = [...(results || []), ...recents];
  const toAdd = resultsAndRecents.find((res) => res.id === id);

  if (toAdd) {
    return toAdd;
  }

  const rephrased = resultsAndRecents.reduce<IRephrasedResult[]>(
    (all, current) => all.concat(current.rephrasedResults),
    [],
  );

  return rephrased.find((res) => res.id === id);
}

function textAddedToResults(results: IResult[], res: IResult | IRephrasedResult) {
  const withRephrases = results.reduce<IRephrasedResult[]>(
    (all, current) => all.concat(current.rephrasedResults),
    [...results],
  );

  return Boolean(withRephrases.find((recent) => recent.result === res?.result));
}

const useStore = create<Store>()(
  persist(
    (set, get) => {
      let genericErrorTimeout: ReturnType<typeof setTimeout>;

      return {
        ...initialState,

        setCreatePageCategoryIndex: (createPageCategoryIndex) => {
          set({ createPageCategoryIndex });
        },

        setResultsAreGenerating: (resultsAreGenerating) => {
          set({ resultsAreGenerating });
        },

        setMoreResultsAreGenerating: (moreResultsAreGenerating) => {
          set({ moreResultsAreGenerating });
        },

        addResults: ({ category, newResults, replace }) => {
          const { results } = get();
          const categoryResults = results[category];

          if (!replace) {
            newResults = categoryResults.concat(newResults);
          }

          set({
            results: {
              ...results,
              [category]: newResults,
            },
          });
        },

        addRephrases: ({ category, rephrasedResults, id }) => {
          const { results, recents } = get();
          const resultCategory = results[category];
          const newResultCategory = resultCategory.map((res) => {
            return res.id === id ? { ...res, rephrasedResults } : res;
          });
          const newRecents = recents.map((res) => {
            return res.id === id ? { ...res, rephrasedResults } : res;
          });

          set({
            results: {
              ...results,
              [category]: newResultCategory,
            },
            recents: newRecents,
          });
        },

        setIsRephrasing: ({ category, id, value }) => {
          const { results, recents } = get();
          const resultCategory = results[category];
          const newResultCategory = resultCategory.map((res) => {
            return res.id === id ? { ...res, isRephrasing: value } : res;
          });
          const newRecents = recents.map((res) => {
            return res.id === id ? { ...res, isRephrasing: value } : res;
          });

          set({
            results: {
              ...results,
              [category]: newResultCategory,
            },
            recents: newRecents,
          });
        },

        addRecents: ({ category, id }) => {
          const { recents, results } = get();
          const categoryResults = results[category];
          const toAdd = findResultToBeAddedToRecents(categoryResults, recents, id);
          const isAdded = textAddedToResults(recents, toAdd);

          if (!isAdded) {
            const newResult = {
              ...toAdd,
              category,
              id: nanoid(),
              type: ResultType.Recent,
              rephrasedResults: [],
            };

            recents.unshift(newResult);

            set({
              recents: [...recents],
            });
          }
        },

        showValidationError: (validationError) => {
          set({
            validationError,
          });
        },

        showGenericError: (genericError) => {
          clearTimeout(genericErrorTimeout);

          set({
            genericError,
          });

          if (genericError) {
            genericErrorTimeout = setTimeout(() => {
              set({
                genericError: '',
              });
            }, 10000);
            handleScroll('qt_main_content');
          }
        },

        setFormValues: (formValues) => {
          set({ formValues });
        },
        setFormValue: ({ name, value }) => {
          const { formValues } = get();

          set({ formValues: { ...formValues, [name]: value } });
        },
      };
    },
    {
      name: '/text/ai-writer/',
    },
  ),
);

export default useStore;
